import { SagaIterator } from "redux-saga";
import { call, SagaGenerator, select, takeEvery } from "typed-redux-saga";
import { Action, AnyAction, Success } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Selector, StoreSagas } from "@com.mgmtp.a12.client/client-core/lib/core/store";
import { OverviewActivity } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { Events as OverviewEngineEvents } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/store/internal/actions";
import { OverviewEngineActions } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions/internal/actions";
import { TreeEngineActions } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";
import { NotificationActions, NotificationSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/notification";

import { TEXTBAUSTEINE_READONLY_TREE } from "../../utilities/variables/documentModelName";
import { setFocusToElementIfAllowed } from "../../utilities/domUtils";

import { ANTRAG_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "../wiedervorlagen/constants";

export function getViewHtmlId(activityId: string | undefined): string | undefined {
	return activityId ? `view-${activityId}` : undefined;
}

/**
 * Checks if there exists a div that surrounds the activity. If an div is found focuses either the whole activity
 * or the last focussed element. This should only be the case if a activity is closed. Always focuses the whole activity when a activity is pushed.
 * Differentiates between newly created documents and existing documents. If a document got modified we focus the row in the overview.
 * @param activityId
 * @param prevElement
 * @param instance
 */
export function* focusViewPart(activityId: string, prevElement: Element | null, instance?: string) {
	const data = yield* select(ActivitySelectors.data(activityId));
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (activity?.descriptor.engine === "tree" && prevElement instanceof HTMLElement) {
		setFocusToElementIfAllowed(prevElement);
	}

	const observer = new MutationObserver(() => {
		const id = getViewHtmlId(activityId);
		const viewComponent = id ? document.getElementById(id) : null;
		if (viewComponent === null) {
			return;
		}
		observer.disconnect();
		if (instance === undefined) {
			setFocusToElementIfAllowed(viewComponent);
			return;
		}
		if (
			[WIEDERVORLAGEN_OVERVIEW_ACTIVITY, NEW_INSTANCE_IDENTIFIER].includes(instance) &&
			prevElement instanceof HTMLElement
		) {
			setFocusToElementIfAllowed(prevElement);
			return;
		}

		if (activityId === ANTRAG_DETAIL_ACTIVITY_ID) {
			return;
		}

		if (OverviewActivity.Data.DocumentListData.isInstance(data)) {
			const index = data.documents.findIndex(d => d?.id === instance);
			const row = viewComponent.querySelectorAll(`[data-role='table-body-row']`)[index];
			if (row instanceof HTMLElement) {
				setFocusToElementIfAllowed(row);
				return;
			}
			const table = viewComponent.querySelector("[data-role='table']");
			if (table instanceof HTMLElement) {
				setFocusToElementIfAllowed(table);
				return;
			}
		}
	});

	observer.observe(document, {
		subtree: true,
		childList: true
	});
}

export function createFocusSaga(): (() => SagaIterator<void>)[] {
	let previouslyEditedInstance: string | undefined;
	const prevActiveElements: (Element | null)[] = [];

	const pushToPrevActiveElements = (element: Element | null) => {
		prevActiveElements.push(element);

		if (prevActiveElements.length > 5) {
			// more than 5 stored prev active elements aren't necessary
			prevActiveElements.shift();
		}
	};

	return [
		function* handleActivityCreationFocus(): SagaIterator<void> {
			/**
			 * Checks if the pushed activity is a form, a workflow form, an overview or a tree. Always sets the previously focussed element to eventually return to it.
			 * Also sets the previously edited instance to identify if we need to focus a row or a button after closing.
			 */
			yield* takeEvery(ActivityActions.push, function* (action) {
				const activeElement = document.activeElement;
				// focus on body is the fallback and doesn't need to be added
				if (activeElement && activeElement.localName !== "body") {
					pushToPrevActiveElements(activeElement);
				}

				// TODO: Fokus wird bei Viewwechsel über Navbar sporadisch auf body gesetzt,
				//  obwohl viewComponent korrekt ermittelt wird (liegt eventuell an Progress Indicator)
				yield* call(focusViewPart, action.payload.activity.id, null);
				if (action.payload.activity.descriptor.instance) {
					previouslyEditedInstance = action.payload.activity.descriptor.instance;
				} else if (!action.payload.activity.descriptor.rootInstance) {
					previouslyEditedInstance = undefined;
				}
			});
		},
		function* handleActivityCancelFocus(): SagaIterator<void> {
			/** Handles the cancel actions. Checks if the latest activity, that is open after closing is a workflow form or something else.
			 * If it is a workflow form we have to also check if the closed activity is a wiedervorlagen activity. If this is the
			 * case we need to focus the Button that opened it. Alternative functionality for other cases can be added if necessary.
			 */
			yield* takeEvery(ActivityActions.cancel, function* (action) {
				const latestActivity = yield* select(ActivitySelectors.latestActivity());
				if (latestActivity === undefined) {
					return;
				}

				if (latestActivity.descriptor.instance) {
					if (action.payload.activityId === WIEDERVORLAGEN_OVERVIEW_ACTIVITY) {
						yield* call(
							focusViewPart,
							latestActivity.id,
							prevActiveElements.pop() ?? null,
							WIEDERVORLAGEN_OVERVIEW_ACTIVITY
						);
					}

					previouslyEditedInstance = latestActivity.descriptor.instance;
				} else {
					yield* call(
						focusViewPart,
						latestActivity.id,
						prevActiveElements.pop() ?? null,
						previouslyEditedInstance
					);
				}
			});
		},
		function* handleCommitDoneFocus(): SagaIterator<void> {
			yield* takeEvery(
				ActivityActions.commit.done,
				function* (action: Action<Success<ActivityActions.CommitPayload, { instance?: string }>>) {
					const latestActivity = yield* select(ActivitySelectors.latestActivity());
					if (!latestActivity || latestActivity.descriptor.instance) {
						return;
					}

					const isLoading = yield* call(() =>
						StoreSagas.waitForStateChange(overviewLoadingState(latestActivity.id, "loading"))
					);
					if (!isLoading) {
						return;
					}

					const isLoaded = yield* call(() =>
						StoreSagas.waitForStateChange(overviewLoadingState(latestActivity.id, "loaded"))
					);

					if (isLoaded) {
						const docId = action.payload.result.instance;
						const latestActivity = yield* select(ActivitySelectors.latestActivity());
						if (docId && latestActivity) {
							yield* call(focusViewPart, latestActivity.id, prevActiveElements.pop() ?? null, docId);
						}
					}
				}
			);
		},
		/** Overview engine: restore focus to column headers after sorting */
		function* handleSortingFocus(): SagaIterator<void> {
			yield* takeEvery(
				(action: AnyAction) =>
					OverviewEngineActions.event.match(action) &&
					(OverviewEngineEvents.onSorted.match(action.payload.engineAction) ||
						OverviewEngineEvents.onFilterChanged.match(action.payload.engineAction)),
				function* (action: Action<OverviewEngineActions.EventPayload>) {
					const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
					if (!activity) {
						return;
					}

					const activeElement = document.activeElement;

					const isLoading = yield* call(() =>
						StoreSagas.waitForStateChange(overviewLoadingState(activity.id, "loading"))
					);
					if (!isLoading) {
						return;
					}

					const isLoaded = yield* call(() =>
						StoreSagas.waitForStateChange(overviewLoadingState(activity.id, "loaded"))
					);

					if (isLoaded) {
						const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
						if (activity && activeElement instanceof HTMLElement) {
							setFocusToElementIfAllowed(activeElement);
						}
					}
				}
			);
		},
		function* handleTextbausteinFocus(): SagaGenerator<void> {
			// Sets the focus to the Textbaustein modal (from module "Information nachfordern") container div instead of the body.
			yield* takeEvery(
				TreeEngineActions.setDataHolders.match,
				function* (action: Action<TreeEngineActions.SetNodesAndOperationsDataHoldersPayload>) {
					const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
					if (activity && activity.descriptor.model == TEXTBAUSTEINE_READONLY_TREE) {
						yield* call(focusViewPart, activity.id, null);
					}
				}
			);
		},
		function* handleEldoradoModalFocus(): SagaGenerator<void> {
			// Sets the focus to the Eldorado archival modal container div instead of the body.
			yield* takeEvery(
				ActivityActions.setData.match,
				function* (action: Action<TreeEngineActions.SetNodesAndOperationsDataHoldersPayload>) {
					const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
					if (activity && activity.descriptor.modal === "EldoradoArchival") {
						yield* call(focusViewPart, activity.id, null);
					}
				}
			);
		},
		/**
		 * when the focus is set to a toast and the toast is closed we have to set the focus back to present modals
		 */
		function* handleToastExitFocus(): SagaGenerator<void> {
			yield* takeEvery(NotificationActions.remove, function* (action) {
				const notifications = yield* select(NotificationSelectors.notifications());
				if (notifications.length !== 0) {
					return;
				}
				const modalOverlays = document.querySelectorAll(`[data-role='modal-overlay-content']`);
				if (modalOverlays.length !== 0) {
					const item = modalOverlays.item(0);
					if (item instanceof HTMLElement) {
						item.focus();
					}
				}
			});
		}
	];
}

// Die Überprüfung, ob mehr als eine Activity zur Zeit aktiv ist können wir nicht durchführen, da die Wiedervorlagen Overview immer
// mit mehreren anderen Aktivitäten aktiv ist. Falls es zu Problemen kommt muss hier eine Ausnahme für die Wiedervorlagen Overview
// eingebaut werden und der Check auf mehrere Aktivitäten wieder eingeführt werden.
function overviewLoadingState(
	activityId: string,
	awaitedState: Activity.LoadingState
): Selector<{ stateChanged: boolean; returnValue: boolean }> {
	return state => {
		const activity = ActivitySelectors.activityById(activityId)(state);

		if (activity === undefined || activity.descriptor.instance) {
			return { stateChanged: true, returnValue: false };
		}

		const loadingState = ActivitySelectors.loadingState(activity);
		if (loadingState.state === "error") {
			return { stateChanged: true, returnValue: false };
		} else if (loadingState.state === awaitedState) {
			return { stateChanged: true, returnValue: true };
		}
		return { stateChanged: false, returnValue: false };
	};
}
